const AntiAsthmaticNotes = `
<div class="space-y-8 animate-fadeIn">
    
    <div>
        <h2 class="text-2xl font-black text-blue-900 mb-3 border-b-2 border-slate-200 pb-2">1. Pharmacology of Anti-Asthmatic Drugs</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            <strong>Asthma</strong> is a chronic inflammatory disease of the airways. It is characterized by acute episodes of bronchial hyper-responsiveness, mucosal edema, and excessive mucus production, leading to reversible airflow obstruction.
        </p>
    </div>

    <div class="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl shadow-sm">
        <h3 class="text-[16px] font-black text-blue-800 mb-3 flex items-center gap-2">
            <span class="text-xl">🧬</span> Pathophysiology of Asthma
        </h3>
        <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-700 leading-relaxed">
            <li><strong>Sensitization:</strong> Exposure to an allergen causes production of IgE antibodies, which bind to the surface of mast cells in the airway mucosa.</li>
            <li><strong>Early Phase (Immediate Response):</strong> Re-exposure to the allergen cross-links the IgE antibodies, causing mast cell degranulation. This releases preformed mediators like <strong>Histamine, Proteases, and Chemotactic factors</strong>, leading to sudden bronchoconstriction.</li>
            <li><strong>Late Phase (Inflammatory Response):</strong> Membrane phospholipids are cleaved to produce <strong>Leukotrienes (LTC4, LTD4, LTE4)</strong> and <strong>Prostaglandins (PGD2)</strong>. This recruits eosinophils and T-lymphocytes, causing mucosal edema, epithelial damage, and hyper-secretion of thick mucus.</li>
        </ul>
    </div>

    <div>
        <h3 class="text-lg font-bold text-slate-800 mb-4">Classification of Anti-Asthmatic Drugs</h3>
        <div class="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-slate-100 text-slate-700 border-b border-slate-200">
                    <tr>
                        <th class="px-5 py-3 font-bold uppercase tracking-wider w-1/3">Drug Class</th>
                        <th class="px-5 py-3 font-bold uppercase tracking-wider w-2/3">Examples</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-600">
                    <tr class="hover:bg-slate-50 transition-colors">
                        <td class="px-5 py-4 font-bold text-blue-900 align-top">1. Bronchodilators</td>
                        <td class="px-5 py-4">
                            <ul class="list-disc pl-4 space-y-1">
                                <li><b>&beta;2-Sympathomimetics (SABA):</b> Salbutamol, Terbutaline</li>
                                <li><b>&beta;2-Sympathomimetics (LABA):</b> Salmeterol, Formoterol</li>
                                <li><b>Methylxanthines:</b> Theophylline, Aminophylline, Doxofylline</li>
                                <li><b>Anticholinergics:</b> Ipratropium bromide, Tiotropium bromide</li>
                            </ul>
                        </td>
                    </tr>
                    <tr class="hover:bg-slate-50 transition-colors">
                        <td class="px-5 py-4 font-bold text-blue-900 align-top">2. Leukotriene Antagonists</td>
                        <td class="px-5 py-4">Montelukast, Zafirlukast</td>
                    </tr>
                    <tr class="hover:bg-slate-50 transition-colors">
                        <td class="px-5 py-4 font-bold text-blue-900 align-top">3. Mast Cell Stabilizers</td>
                        <td class="px-5 py-4">Sodium Cromoglycate, Ketotifen</td>
                    </tr>
                    <tr class="hover:bg-slate-50 transition-colors">
                        <td class="px-5 py-4 font-bold text-blue-900 align-top">4. Corticosteroids</td>
                        <td class="px-5 py-4">
                            <ul class="list-disc pl-4 space-y-1">
                                <li><b>Inhalational (ICS):</b> Beclomethasone, Budesonide, Fluticasone</li>
                                <li><b>Systemic:</b> Hydrocortisone, Prednisolone, Dexamethasone</li>
                            </ul>
                        </td>
                    </tr>
                    <tr class="hover:bg-slate-50 transition-colors">
                        <td class="px-5 py-4 font-bold text-blue-900 align-top">5. Anti-IgE Antibody</td>
                        <td class="px-5 py-4">Omalizumab</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div>
        <h3 class="text-lg font-bold text-slate-800 mb-4 border-b border-slate-200 pb-2">Detailed Pharmacology</h3>
        
        <div class="grid grid-cols-1 gap-6">
            
            <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <h4 class="text-[17px] font-black text-blue-700 mb-2">A. &beta;2-Sympathomimetics (&beta;2-Agonists)</h4>
                <p class="text-[13px] text-slate-700 mb-3 leading-relaxed text-justify">
                    These are the most effective and widely used bronchodilators. They are classified into Short-Acting (SABA) used as "Relievers" during acute attacks, and Long-Acting (LABA) used as "Controllers" for maintenance.
                </p>
                <div class="bg-slate-50 p-4 rounded border border-slate-100 text-[13px] text-slate-700 mb-3">
                    <p class="font-bold text-blue-900 mb-1">Mechanism of Action (MOA):</p>
                    <ul class="list-disc pl-5 space-y-1">
                        <li>They selectively bind to and stimulate &beta;2-adrenergic receptors present on bronchial smooth muscle cells.</li>
                        <li>This activates the enzyme <strong>Adenylyl Cyclase</strong> via Gs-proteins.</li>
                        <li>Adenylyl cyclase converts ATP to <strong>cyclic AMP (cAMP)</strong>.</li>
                        <li>Increased intracellular cAMP activates Protein Kinase A (PKA), which decreases intracellular calcium concentration and inhibits myosin light chain kinase.</li>
                        <li>Result: Relaxation of bronchial smooth muscles (Bronchodilation).</li>
                    </ul>
                </div>
                <div class="text-[12px] text-slate-600">
                    <p><b>Therapeutic Uses:</b> Acute severe asthma (Salbutamol inhalation), COPD, Exercise-induced asthma.</p>
                    <p class="mt-1 text-red-700"><b>Adverse Effects:</b> Fine muscle tremors (due to &beta;2 receptor stimulation in skeletal muscles), Tachycardia/Palpitation, Hypokalemia.</p>
                </div>
            </div>

            <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <h4 class="text-[17px] font-black text-blue-700 mb-2">B. Methylxanthines (Theophylline)</h4>
                <p class="text-[13px] text-slate-700 mb-3 leading-relaxed text-justify">
                    Plant-derived alkaloids historically used for asthma, but their use has declined due to a narrow therapeutic index and risk of toxicity.
                </p>
                <div class="bg-slate-50 p-4 rounded border border-slate-100 text-[13px] text-slate-700 mb-3">
                    <p class="font-bold text-blue-900 mb-1">Mechanism of Action (MOA):</p>
                    <ul class="list-disc pl-5 space-y-1">
                        <li><strong>PDE Inhibition:</strong> They inhibit the enzyme Phosphodiesterase (PDE-4), preventing the degradation of cAMP. Elevated cAMP causes bronchodilation.</li>
                        <li><strong>Adenosine Antagonism:</strong> They block adenosine A1 receptors, which are responsible for bronchoconstriction and histamine release.</li>
                    </ul>
                </div>
                <div class="text-[12px] text-slate-600">
                    <p><b>Pharmacokinetics:</b> Very narrow margin of safety. Metabolized extensively in the liver by CYP1A2. Drugs like Erythromycin and Ciprofloxacin inhibit its metabolism, leading to toxicity.</p>
                    <p class="mt-1 text-red-700"><b>Adverse Effects:</b> Nausea, vomiting, headache, insomnia. At toxic doses: Cardiac arrhythmias and seizures (convulsions).</p>
                </div>
            </div>

            <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <h4 class="text-[17px] font-black text-blue-700 mb-2">C. Anticholinergics</h4>
                <div class="bg-slate-50 p-4 rounded border border-slate-100 text-[13px] text-slate-700 mb-3">
                    <p class="font-bold text-blue-900 mb-1">Mechanism of Action (MOA):</p>
                    <p class="mb-2">Ipratropium and Tiotropium competitively block <strong>Muscarinic (M3) receptors</strong> on bronchial smooth muscles. This blocks the vagal reflex, preventing acetylcholine-induced bronchoconstriction and reducing mucus hypersecretion.</p>
                </div>
                <div class="text-[12px] text-slate-600">
                    <p><b>Uses:</b> They are the drug of choice for <strong>COPD</strong> (Chronic Obstructive Pulmonary Disease) and are used as an adjuvant in severe asthma.</p>
                    <p class="mt-1 text-red-700"><b>Adverse Effects:</b> Dryness of mouth, bad taste, scratchy throat. Very few systemic effects because they are quaternary ammonium compounds (poorly absorbed).</p>
                </div>
            </div>

            <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <h4 class="text-[17px] font-black text-blue-700 mb-2">D. Corticosteroids (Glucocorticoids)</h4>
                <p class="text-[13px] text-slate-700 mb-3 leading-relaxed text-justify">
                    Corticosteroids are not bronchodilators; they are the most potent and consistently effective <strong>anti-inflammatory agents</strong> used in asthma.
                </p>
                <div class="bg-slate-50 p-4 rounded border border-slate-100 text-[13px] text-slate-700 mb-3">
                    <p class="font-bold text-blue-900 mb-1">Mechanism of Action (MOA):</p>
                    <ul class="list-disc pl-5 space-y-1">
                        <li>They enter the target cell and bind to cytosolic glucocorticoid receptors.</li>
                        <li>The drug-receptor complex enters the nucleus and alters gene transcription.</li>
                        <li>They up-regulate the synthesis of <strong>Lipocortin-1 (Annexin A1)</strong>.</li>
                        <li>Lipocortin-1 strongly inhibits the enzyme <strong>Phospholipase A2</strong>.</li>
                        <li>This blocks the release of arachidonic acid from cell membranes, completely shutting down the synthesis of highly inflammatory <strong>Leukotrienes and Prostaglandins</strong>.</li>
                    </ul>
                </div>
                <div class="text-[12px] text-slate-600">
                    <p><b>Inhaled Corticosteroids (ICS):</b> Budesonide, Fluticasone. Used for long-term prophylaxis.</p>
                    <p class="mt-1 text-red-700"><b>Adverse Effects of ICS:</b> Oropharyngeal candidiasis (Thrush), dysphonia (hoarseness of voice). <i>Patient counseling: Always rinse mouth and gargle after inhalation to prevent candidiasis.</i></p>
                </div>
            </div>

            <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <h4 class="text-[17px] font-black text-blue-700 mb-2">E. Leukotriene Receptor Antagonists</h4>
                <div class="bg-slate-50 p-4 rounded border border-slate-100 text-[13px] text-slate-700 mb-3">
                    <p class="font-bold text-blue-900 mb-1">Mechanism of Action (MOA):</p>
                    <p>Montelukast and Zafirlukast act as highly selective and competitive antagonists of the <strong>Cysteinyl Leukotriene-1 (CysLT1) receptor</strong>. By blocking this receptor, they prevent leukotrienes (LTC4, LTD4, LTE4) from inducing bronchospasm, vascular permeability, and mucosal edema.</p>
                </div>
                <div class="text-[12px] text-slate-600">
                    <p><b>Uses:</b> Maintenance therapy for mild to moderate asthma. Especially effective in <strong>Aspirin-induced asthma</strong> and Exercise-induced asthma.</p>
                    <p class="mt-1 text-red-700"><b>Adverse Effects:</b> Very safe overall. Mild headache, GI disturbances, and rarely Churg-Strauss syndrome.</p>
                </div>
            </div>

        </div>
    </div>
</div>
`;

export default AntiAsthmaticNotes;